import React, { useEffect } from 'react';
import {
  ArrowRight,
  Bell,
  BookOpen,
  CheckCircle2,
  Database,
  ExternalLink,
  FileSearch,
  GitCompareArrows,
  Globe,
  HeartPulse,
  Key,
  Mail,
  ShieldCheck,
  Webhook
} from 'lucide-react';

const SITE_URL = 'https://fiscal.mateusemth.dev';
const API_ACCOUNT_URL = '/conta-api';
const API_DOCS_URL = '/api-docs';

const quickStats = [
  ['Portais', 'NF-e, NFC-e, CT-e, MDF-e, BP-e, NFCom e outros'],
  ['Conteúdo', 'Notas técnicas, manuais, schemas XML e publicações'],
  ['Operação', 'Status, manutenção, contingência, alertas e API']
];

const featureCards = [
  {
    title: 'Documentos técnicos',
    description: 'Consulta publicações fiscais, notas técnicas, manuais, schemas XML e arquivos dos portais SEFAZ e SVRS.',
    icon: FileSearch,
    tone: 'text-teal-700 bg-teal-50 border-teal-100 dark:text-teal-300 dark:bg-teal-900/20 dark:border-teal-900/40'
  },
  {
    title: 'Comparador de versões',
    description: 'Ajuda a entender diferenças entre versões de uma mesma série de documento fiscal com resumo objetivo das mudanças.',
    icon: GitCompareArrows,
    tone: 'text-sky-700 bg-sky-50 border-sky-100 dark:text-sky-300 dark:bg-sky-900/20 dark:border-sky-900/40'
  },
  {
    title: 'Status dos serviços',
    description: 'Acompanha disponibilidade, manutenção e contingência dos serviços monitorados para reduzir consulta manual.',
    icon: HeartPulse,
    tone: 'text-emerald-700 bg-emerald-50 border-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/20 dark:border-emerald-900/40'
  },
  {
    title: 'Alertas e integrações',
    description: 'Permite acompanhar novidades por e-mail, webhooks e API para equipes técnicas, fiscais e de suporte.',
    icon: Bell,
    tone: 'text-blue-700 bg-blue-50 border-blue-100 dark:text-blue-300 dark:bg-blue-900/20 dark:border-blue-900/40'
  }
];

const workflowSteps = [
  {
    title: 'Escolha o portal fiscal',
    description: 'Selecione MDF-e, NFC-e, CT-e, NF-e ou outro portal SVRS/SEFAZ disponível no monitor.'
  },
  {
    title: 'Consulte documentos e histórico',
    description: 'O sistema busca documentos recentes, organiza por categoria e usa a base persistida quando houver histórico.'
  },
  {
    title: 'Compare versões importantes',
    description: 'Ao encontrar uma série comparável, abra o comparador para ver versões, diferenças e leitura assistida por IA.'
  },
  {
    title: 'Receba alertas e automatize',
    description: 'Use notificações, webhooks e API para não depender de conferência manual dos portais oficiais.'
  }
];

const audienceItems = [
  'Times de tecnologia que implementam NF-e, NFC-e, CT-e, MDF-e e outros documentos eletrônicos.',
  'Áreas fiscais que precisam acompanhar notas técnicas, manuais e alterações operacionais.',
  'Suporte e operação que monitoram disponibilidade dos serviços fiscais e contingências.',
  'Produtos SaaS fiscais que querem integrar publicações e alertas em fluxos internos.'
];

function updateMetaTag(selector, attribute, value) {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
}

function PublicHeader() {
  return (
    <header className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <a href="/" className="flex min-w-0 items-center gap-3">
          <img src="/icon-master.png" alt="" aria-hidden="true" className="h-11 w-11 shrink-0 rounded-2xl object-contain" />
          <div className="min-w-0">
            <p className="truncate text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">Monitor Fiscal</p>
            <p className="truncate text-[11px] font-bold text-gray-500 dark:text-gray-400">SEFAZ, SVRS, alertas e API</p>
          </div>
        </a>

        <nav className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
          <a href="#sobre" className="rounded-xl px-3 py-2 hover:bg-gray-100 hover:text-teal-700 dark:hover:bg-gray-700 dark:hover:text-teal-300">Sobre</a>
          <a href="#como-funciona" className="rounded-xl px-3 py-2 hover:bg-gray-100 hover:text-teal-700 dark:hover:bg-gray-700 dark:hover:text-teal-300">Fluxo</a>
          <a href="#recursos" className="rounded-xl px-3 py-2 hover:bg-gray-100 hover:text-teal-700 dark:hover:bg-gray-700 dark:hover:text-teal-300">Recursos</a>
          <a href="#faq" className="rounded-xl px-3 py-2 hover:bg-gray-100 hover:text-teal-700 dark:hover:bg-gray-700 dark:hover:text-teal-300">FAQ</a>
        </nav>

        <a
          href="/"
          className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700"
        >
          Abrir monitor
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </header>
  );
}

function SectionHeader({ eyebrow, title, children }) {
  return (
    <div className="max-w-3xl">
      <p className="text-[10px] font-black uppercase tracking-widest text-teal-700 dark:text-teal-300">{eyebrow}</p>
      <h2 className="mt-2 text-xl font-black tracking-tight text-gray-950 dark:text-white sm:text-2xl">{title}</h2>
      {children ? (
        <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
          {children}
        </p>
      ) : null}
    </div>
  );
}

function BrandPanel() {
  return (
    <aside className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-col items-center text-center">
        <div className="relative flex h-48 w-48 items-center justify-center">
          <div className="absolute h-40 w-40 rounded-full bg-teal-500/10 blur-2xl dark:bg-teal-300/10" />
          <img
            src="/icon-master.png"
            alt="Ícone do Monitor Fiscal"
            className="relative h-36 w-36 select-none rounded-3xl object-contain drop-shadow-xl"
            draggable={false}
          />
        </div>

        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Monitor Fiscal</p>
        <h2 className="mt-2 text-lg font-black text-gray-900 dark:text-white">Monitorando os sinais fiscais.</h2>
        <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">
          A ideia do projeto é simples: deixar as mudanças fiscais menos espalhadas e mais acionáveis.
        </p>
      </div>

      <div className="mt-5 grid gap-2">
        {quickStats.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">{label}</p>
            <p className="mt-1 text-xs font-bold leading-5 text-gray-700 dark:text-gray-200">{value}</p>
          </div>
        ))}
      </div>
    </aside>
  );
}

function PublicInfoPage() {
  useEffect(() => {
    const title = 'Sobre o Monitor Fiscal - Documentos SEFAZ, SVRS e API fiscal';
    const description = 'Conheça o Monitor Fiscal: como acompanhar documentos técnicos fiscais, notas técnicas, schemas XML, status SEFAZ/SVRS, comparações e alertas.';

    document.title = title;
    updateMetaTag('meta[name="description"]', 'content', description);
    updateMetaTag('link[rel="canonical"]', 'href', `${SITE_URL}/sobre`);
    updateMetaTag('meta[property="og:title"]', 'content', title);
    updateMetaTag('meta[property="og:description"]', 'content', description);
    updateMetaTag('meta[property="og:url"]', 'content', `${SITE_URL}/sobre`);
    updateMetaTag('meta[name="twitter:title"]', 'content', title);
    updateMetaTag('meta[name="twitter:description"]', 'content', description);

    if (window.location.hash) {
      const sectionId = decodeURIComponent(window.location.hash.slice(1));
      window.requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ block: 'start' });
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-3 text-gray-900 dark:bg-gray-900 dark:text-gray-100 sm:p-4 lg:p-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <PublicHeader />

        <main className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex min-w-0 flex-col gap-4">
            <section id="sobre" className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-100 bg-teal-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-teal-700 dark:border-teal-900/50 dark:bg-teal-900/20 dark:text-teal-300">
                <BookOpen className="h-3.5 w-3.5" />
                Sobre o Monitor Fiscal
              </div>

              <h1 className="mt-5 max-w-3xl text-2xl font-black tracking-tight text-gray-950 dark:text-white sm:text-4xl">
                Monitoramento fiscal para acompanhar SEFAZ e SVRS sem garimpo manual.
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-gray-600 dark:text-gray-300 sm:text-base">
                O Monitor Fiscal centraliza documentos técnicos, notas técnicas, manuais, schemas XML, status de serviços e alertas dos portais fiscais. A proposta é dar contexto rápido para equipes que precisam agir quando uma publicação ou indisponibilidade muda a rotina fiscal.
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700"
                >
                  Abrir monitor
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={API_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 text-[11px] font-black uppercase tracking-widest text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Docs da API
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={API_ACCOUNT_URL}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 text-[11px] font-black uppercase tracking-widest text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Conta da API
                  <Key className="h-4 w-4" />
                </a>
              </div>
            </section>

            <section id="como-funciona" className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <SectionHeader eyebrow="Como funciona" title="Da consulta ao alerta, o fluxo é direto.">
                O monitor junta consulta operacional, histórico e automação para reduzir o tempo entre uma publicação fiscal e a ação da equipe.
              </SectionHeader>

              <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {workflowSteps.map((step, index) => (
                  <article key={step.title} className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-900 text-xs font-black text-white dark:bg-white dark:text-gray-950">
                      {index + 1}
                    </div>
                    <h3 className="mt-4 text-sm font-black text-gray-950 dark:text-white">{step.title}</h3>
                    <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">{step.description}</p>
                  </article>
                ))}
              </div>
            </section>

            <section id="recursos" className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <SectionHeader eyebrow="Recursos" title="O que o visitante encontra ao abrir o Monitor Fiscal.">
                A interface foi pensada para consulta recorrente: muita informação fiscal, mas organizada para leitura rápida, comparação e acompanhamento.
              </SectionHeader>

              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {featureCards.map(({ title, description, icon: Icon, tone }) => (
                  <article key={title} className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                    <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${tone}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-base font-black text-gray-950 dark:text-white">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{description}</p>
                  </article>
                ))}
              </div>
            </section>

            <section className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                <SectionHeader eyebrow="Para quem é" title="Feito para quem vive a rotina fiscal digital.">
                  O Monitor Fiscal é uma ferramenta independente para leitura e automação. Ele não substitui os portais oficiais nem documentos normativos, mas ajuda a encontrar sinais importantes mais rápido.
                </SectionHeader>

                <div className="grid gap-3">
                  {audienceItems.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-600 dark:text-teal-300" />
                      <p className="text-sm leading-6 text-gray-700 dark:text-gray-200">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="faq" className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <SectionHeader eyebrow="FAQ" title="Perguntas comuns antes de usar." />

              <div className="mt-6 grid gap-3 md:grid-cols-3">
                <article className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                  <ShieldCheck className="h-5 w-5 text-teal-600 dark:text-teal-300" />
                  <h3 className="mt-3 text-sm font-black text-gray-950 dark:text-white">É um serviço oficial?</h3>
                  <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">Não. O Monitor Fiscal é uma ferramenta independente que organiza informações públicas e operacionais dos portais monitorados.</p>
                </article>
                <article className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                  <Globe className="h-5 w-5 text-sky-600 dark:text-sky-300" />
                  <h3 className="mt-3 text-sm font-black text-gray-950 dark:text-white">Quais portais cobre?</h3>
                  <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">O foco atual é SEFAZ e SVRS, com portais como MDF-e, NFC-e, CT-e, NF-e, BP-e, NF3-e, NFCom, NFF e NFAG.</p>
                </article>
                <article className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex gap-2">
                    <Mail className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                    <Webhook className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                  </div>
                  <h3 className="mt-3 text-sm font-black text-gray-950 dark:text-white">Dá para integrar?</h3>
                  <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">
                    Sim. O projeto expõe API, documentação interativa, alertas por e-mail e webhooks para apoiar fluxos internos.
                  </p>
                </article>
              </div>
            </section>
          </div>

          <div className="lg:sticky lg:top-8 lg:self-start">
            <BrandPanel />

            <section className="mt-4 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Integração</p>
              <h2 className="mt-2 text-base font-black text-gray-900 dark:text-white">API pública com conta própria.</h2>
              <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">
                Crie sua conta, confirme o e-mail, gere sua API Key e consulte documentos, status e notificações.
              </p>
              <div className="mt-4 grid gap-2">
                <a
                  href={API_ACCOUNT_URL}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700"
                >
                  <Key className="h-4 w-4" />
                  Conta da API
                </a>
                <a
                  href={API_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 text-[10px] font-black uppercase tracking-widest text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <BookOpen className="h-4 w-4" />
                  Documentação
                </a>
              </div>
            </section>

            <section className="mt-4 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-300">
                  <Database className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Histórico</p>
                  <p className="mt-1 text-sm font-bold leading-6 text-gray-700 dark:text-gray-200">
                    A base persistida ajuda a consultar documentos já monitorados mesmo quando o portal externo oscila.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

    </div>
  );
}

export default PublicInfoPage;
